import { useRef, useState } from "react";
import { Icon } from "../Icon";
import { LOCALES, useLang, useT } from "../i18n";

// The letter to the future first; asking for a reply last, after the simpler stories (reviews/06-action-plan.md 3.2).
const FEATURES = ["future", "group", "public", "sase"] as const;
type Feature = typeof FEATURES[number];

/** A small, manually controlled introduction; reading it never interrupts signing in. */
export function LoginFeatures() {
  const t = useT().login.features;
  const { lang } = useLang();
  const direction = LOCALES[lang].dir === "rtl" ? -1 : 1;
  const [index, setIndex] = useState(0);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const feature = FEATURES[index]!;
  const slide = t.slides[feature];
  const move = (delta: number) => setIndex((current) => (current + delta + FEATURES.length) % FEATURES.length);

  return (
    <section
      className="login-features"
      aria-label={t.label}
      aria-roledescription={t.carousel}
      onKeyDown={(event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          move((event.key === "ArrowLeft" ? -1 : 1) * direction);
        }
      }}
    >
      <div
        className="feature-slide"
        aria-live="polite"
        aria-atomic="true"
        onTouchStart={(event) => {
          const touch = event.touches[0];
          touchStart.current = event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchEnd={(event) => {
          if (touchStart.current !== null && event.changedTouches.length === 1) {
            const touch = event.changedTouches[0]!;
            const distance = touch.clientX - touchStart.current.x;
            if (Math.abs(distance) > 60 && Math.abs(distance) > Math.abs(touch.clientY - touchStart.current.y)) move((distance < 0 ? 1 : -1) * direction);
          }
          touchStart.current = null;
        }}
        onTouchCancel={() => { touchStart.current = null; }}
      >
        <div className="feature-art" aria-hidden="true"><FeatureArt feature={feature} /></div>
        <div className="feature-copy">
          <p className="feature-eyebrow">{slide.eyebrow}</p>
          <h2>{slide.title}</h2>
          <p className="feature-description">{slide.description}</p>
        </div>
      </div>
      <div className="feature-controls">
        <div className="feature-dots" role="group" aria-label={t.choose}>
          {FEATURES.map((key, position) => (
            <button
              key={key}
              type="button"
              aria-label={t.slides[key].eyebrow}
              aria-pressed={position === index}
              onClick={() => setIndex(position)}
            ><span /></button>
          ))}
        </div>
        <span className="feature-count" aria-hidden="true">{String(index + 1).padStart(2, "0")} / 04</span>
        <div className="feature-arrows">
          <button type="button" aria-label={t.previous} onClick={() => move(-1)}><span className="feature-back"><Icon name="chevron" size={16} /></span></button>
          <button type="button" aria-label={t.next} onClick={() => move(1)}><Icon name="chevron" size={16} /></button>
        </div>
      </div>
      <details className="feature-details" key={feature}>
        <summary>{t.more}<Icon name="chevron" size={14} /></summary>
        <div className="feature-details-body">
          {feature === "sase" && <>
            <p className="feature-motto">{t.sealedReply.motto}</p>
            <p>{t.sealedReply.explanation}</p>
            <ul className="feature-use-cases">
              {t.sealedReply.uses.map((use) => <li key={use.label}><strong>{use.label}</strong><span>{use.text}</span></li>)}
            </ul>
          </>}
          <p>{slide.how}</p>
          <p><strong>{t.forWhom}</strong> {slide.audience}</p>
          <p className="feature-example"><span>{t.example}</span> {slide.example}</p>
        </div>
      </details>
    </section>
  );
}

function Envelope({ x, y, small = false }: { x: number; y: number; small?: boolean }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${small ? .72 : 1})`}>
      <rect x="0" y="0" width="112" height="74" rx="6" fill="var(--surface)" stroke="var(--feature-ink)" strokeWidth="1.5" />
      <path d="m2 71 39-32m69 32L71 39" fill="none" stroke="var(--feature-line)" strokeWidth="1.5" />
      <path d="m1 3 49 36q6 5 12 0l49-36" fill="none" stroke="var(--feature-ink)" strokeWidth="1.5" />
      <circle cx="56" cy="39" r="11" fill="var(--feature-warm)" />
      <path d="m52 39 3 3 5-6" fill="none" stroke="var(--feature-ink)" strokeWidth="1.5" strokeLinecap="round" />
    </g>
  );
}

function Clock({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r="28" fill="var(--surface)" stroke="var(--feature-ink)" strokeWidth="1.5" />
      <circle r="22" fill="none" stroke="var(--feature-line)" />
      <path d="M0-13V0l9 6M0-19v2m19 17h-2M0 19v-2m-19-17h2" fill="none" stroke="var(--feature-ink)" strokeWidth="2" strokeLinecap="round" />
      <circle r="2.5" fill="var(--feature-ink)" />
    </g>
  );
}

/** Code-native illustrations share the page's palette, including its dark theme. */
function FeatureArt({ feature }: { feature: Feature }) {
  return (
    <svg viewBox="0 0 480 166" fill="none" focusable="false">
      <ellipse cx="240" cy="148" rx="171" ry="9" fill="var(--feature-line)" opacity=".25" />
      <circle cx="240" cy="81" r="70" fill="var(--feature-glow)" />
      <path d="M60 46h8m-4-4v8m348 61h8m-4-4v8M369 32h6m-3-3v6" stroke="var(--feature-line)" strokeWidth="1.5" strokeLinecap="round" />
      {feature === "sase" && <>
        <path d="M164 47c58-38 133-26 157 12m-12-4 13 5 1-13M316 119c-49 35-125 31-155-2m12 3-13-4-1 13" stroke="var(--feature-ink)" strokeWidth="1.5" strokeDasharray="4 5" strokeLinecap="round" />
        <g transform="rotate(-9 149 93)"><Envelope x={93} y={57} /></g>
        <g transform="rotate(8 333 93)"><Envelope x={277} y={57} /></g>
        <Clock x={240} y={83} />
        <circle cx="372" cy="53" r="13" fill="var(--feature-warm)" />
        <path d="M367 53h10m-4-4 4 4-4 4" stroke="var(--feature-ink)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </>}
      {feature === "future" && <>
        <path d="M153 111c53 34 112 20 155-7m-8-2 10 1-4 10" stroke="var(--feature-ink)" strokeWidth="1.5" strokeDasharray="4 5" strokeLinecap="round" />
        <g transform="rotate(-9 160 75)"><Envelope x={103} y={39} /></g>
        <rect x="278" y="36" width="92" height="94" rx="7" fill="var(--surface)" stroke="var(--feature-ink)" strokeWidth="1.5" />
        <path d="M278 61h92m-71-31v13m50-13v13" stroke="var(--feature-ink)" strokeWidth="2" strokeLinecap="round" />
        <path d="M295 77h9m12 0h9m12 0h9m-51 16h9m12 0h9m12 0h9m-51 16h9" stroke="var(--feature-line)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="338" cy="109" r="12" fill="var(--feature-warm)" />
        <path d="m333 109 3 3 6-7" stroke="var(--feature-ink)" strokeWidth="1.5" strokeLinecap="round" />
        <Clock x={238} y={64} />
      </>}
      {feature === "group" && <>
        <path d="M203 84h55m0 0V37h49m-49 47h49m-49 0v47h49" stroke="var(--feature-ink)" strokeWidth="1.5" strokeDasharray="4 5" />
        <g transform="rotate(-7 153 85)"><Envelope x={97} y={48} /></g>
        {[37, 84, 131].map((y, i) => <g key={y} transform={`translate(328 ${y})`}>
          <circle r="20" fill={i === 1 ? "var(--feature-warm)" : "var(--surface)"} stroke="var(--feature-ink)" strokeWidth="1.5" />
          <circle cy="-5" r="5" stroke="var(--feature-ink)" strokeWidth="1.5" />
          <path d="M-10 12v-2a10 10 0 0 1 20 0v2" stroke="var(--feature-ink)" strokeWidth="1.5" strokeLinecap="round" />
        </g>)}
      </>}
      {feature === "public" && <>
        <circle cx="325" cy="83" r="48" fill="var(--surface)" stroke="var(--feature-ink)" strokeWidth="1.5" />
        <ellipse cx="325" cy="83" rx="21" ry="48" stroke="var(--feature-line)" strokeWidth="1.5" />
        <path d="M277 83h96m-89-25h82m-82 50h82m-41-73v96" stroke="var(--feature-line)" strokeWidth="1.5" />
        <g transform="rotate(-8 157 87)"><Envelope x={101} y={50} /></g>
        <circle cx="205" cy="112" r="22" fill="var(--feature-warm)" stroke="var(--feature-ink)" strokeWidth="1.5" />
        <path d="M199 108v-5a6 6 0 0 1 12 0v5m-14 0h16v13h-16zm8 5v3" stroke="var(--feature-ink)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </>}
    </svg>
  );
}
